import type { Game, GameContext, GameMeta, InputState } from '../types';
import { CyberDefenderEngine } from './CyberDefenderEngine';

/**
 * High-Contrast Retro Vector Canvas Renderer for ASTRO-BUBBLE DEFENDER.
 * Connects the decoupled CyberDefenderEngine logic with HTML5 Canvas 2D graphics.
 */
export class CyberDefenderGame implements Game {
  readonly meta: GameMeta = {
    id: 'cyber-defender',
    title: 'ASTRO-BUBBLE DEFENDER',
    blurb: 'Asteroid + Invaders + Bubble Bobble fusion. Trap threats in bubbles & trigger chain combos!',
    tag: 'ARCADE',
    status: 'playable',
  };

  private engine: CyberDefenderEngine;
  private canvas!: HTMLCanvasElement;
  private ctx2d!: CanvasRenderingContext2D;
  private gameCtx!: GameContext;
  private animFrameId: number | null = null;
  private isDisposed = false;

  constructor() {
    this.engine = new CyberDefenderEngine();
  }

  mount(ctx: GameContext): void {
    this.gameCtx = ctx;
    this.isDisposed = false;

    ctx.mount.innerHTML = '';
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-canvas';
    this.canvas.setAttribute('aria-label', 'Astro-Bubble Defender canvas');
    ctx.mount.appendChild(this.canvas);

    const context = this.canvas.getContext('2d');
    if (!context) {
      throw new Error('Could not acquire 2D Canvas context');
    }
    this.ctx2d = context;

    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 800, rect.height || 600);
    this.engine.initGame();
  }

  update(dt: number, input: InputState): void {
    if (this.isDisposed) return;

    // Sample WebAudio music reactivity bands
    const bands = this.gameCtx?.getBands ? this.gameCtx.getBands() : { energy: 0.2, kick: 0, beat: 0 };

    this.engine.update(dt, input, {
      energy: bands.energy,
      kick: bands.kick,
      beat: bands.beat,
    });

    this.render(bands.energy, bands.kick);
  }

  private render(energy: number, kick: number): void {
    const ctx = this.ctx2d;
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Scale engine space (800x600) to actual canvas dimensions
    const scaleX = w / this.engine.width;
    const scaleY = h / this.engine.height;

    ctx.save();
    ctx.scale(scaleX, scaleY);

    // Dark Arcade Background with dynamic subtle grid
    ctx.fillStyle = '#050711';
    ctx.fillRect(0, 0, this.engine.width, this.engine.height);

    this.drawStarfield(ctx, energy);
    this.drawBarricades(ctx);
    this.drawInvaders(ctx, kick);
    this.drawAsteroids(ctx);
    this.drawBullets(ctx);
    this.drawPowerups(ctx);
    this.drawParticles(ctx);
    this.drawPlayer(ctx, kick);

    ctx.restore();
  }

  private drawStarfield(ctx: CanvasRenderingContext2D, energy: number): void {
    ctx.save();
    ctx.fillStyle = `rgba(0, 255, 200, ${0.15 + energy * 0.25})`;
    for (let i = 0; i < 40; i++) {
      const x = (i * 37 + performance.now() * 0.02) % this.engine.width;
      const y = (i * 53) % this.engine.height;
      ctx.fillRect(x, y, 2, 2);
    }
    ctx.restore();
  }

  private drawPlayer(ctx: CanvasRenderingContext2D, kick: number): void {
    const p = this.engine.player;
    if (p.lives <= 0) return;

    // Flashing when invulnerable
    if (p.invulnTimer > 0 && Math.floor(p.invulnTimer * 10) % 2 === 0) return;

    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);

    const scale = 1 + kick * 0.15;
    ctx.scale(scale, scale);

    // Vector Neon Ship Body
    ctx.strokeStyle = p.spreadTimer > 0 ? '#ff00ff' : p.rapidTimer > 0 ? '#ffff00' : '#00ffff';
    ctx.lineWidth = 2.5;
    ctx.shadowColor = ctx.strokeStyle;
    ctx.shadowBlur = 8;

    ctx.beginPath();
    ctx.moveTo(p.radius + 4, 0);
    ctx.lineTo(-p.radius, -p.radius * 0.75);
    ctx.lineTo(-p.radius * 0.4, 0);
    ctx.lineTo(-p.radius, p.radius * 0.75);
    ctx.closePath();
    ctx.stroke();

    // Shield outline
    if (p.lives > 1) {
      ctx.strokeStyle = `rgba(0, 255, 128, ${0.4 + kick * 0.3})`;
      ctx.beginPath();
      ctx.arc(0, 0, p.radius + 6, 0, Math.PI * 2);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawAsteroids(ctx: CanvasRenderingContext2D): void {
    for (const ast of this.engine.asteroids) {
      ctx.save();
      ctx.translate(ast.x, ast.y);

      if (ast.isTrapped) {
        // Glowing Neon Kinetic Bubble
        ctx.strokeStyle = '#00ffcc';
        ctx.fillStyle = 'rgba(0, 255, 200, 0.2)';
        ctx.lineWidth = 2;
        ctx.shadowColor = '#00ffcc';
        ctx.shadowBlur = 10;

        ctx.beginPath();
        ctx.arc(0, 0, ast.bubbleRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Enclosed Asteroid inside
        ctx.strokeStyle = '#88aaaa';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(0, 0, ast.radius * 0.6, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        // Jagged Asteroid Vector
        ctx.rotate(ast.angle);
        ctx.strokeStyle = ast.tier === 3 ? '#ff3366' : ast.tier === 2 ? '#ff6699' : '#ff99bb';
        ctx.lineWidth = 2;
        ctx.shadowColor = ctx.strokeStyle;
        ctx.shadowBlur = 6;

        ctx.beginPath();
        const points = 7;
        for (let i = 0; i < points; i++) {
          const a = (i / points) * Math.PI * 2;
          const r = ast.radius * (0.8 + Math.sin(i * 3) * 0.2);
          const px = Math.cos(a) * r;
          const py = Math.sin(a) * r;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.stroke();
      }

      ctx.restore();
    }
  }

  private drawInvaders(ctx: CanvasRenderingContext2D, kick: number): void {
    for (const inv of this.engine.invaders) {
      ctx.save();
      ctx.translate(inv.x, inv.y);

      if (inv.isTrapped) {
        // Trapped Invader Bubble
        ctx.strokeStyle = '#ff00aa';
        ctx.fillStyle = 'rgba(255, 0, 170, 0.25)';
        ctx.lineWidth = 2;
        ctx.shadowColor = '#ff00aa';
        ctx.shadowBlur = 12;

        ctx.beginPath();
        ctx.arc(0, 0, inv.bubbleRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      } else {
        // Retro Invader Glyph
        ctx.strokeStyle = '#ffaa00';
        ctx.lineWidth = 2;
        ctx.shadowColor = '#ffaa00';
        ctx.shadowBlur = 6;

        const size = inv.radius * (1 + kick * 0.1);
        ctx.strokeRect(-size / 2, -size / 2, size, size);

        ctx.fillStyle = '#ffaa00';
        ctx.fillRect(-size / 4, -size / 4, size / 2, size / 2);
      }

      ctx.restore();
    }
  }

  private drawBarricades(ctx: CanvasRenderingContext2D): void {
    for (const bar of this.engine.barricades) {
      ctx.save();
      const hpPct = bar.hp / bar.maxHp;
      ctx.fillStyle = `rgba(0, 180, 255, ${0.3 + hpPct * 0.6})`;
      ctx.strokeStyle = '#00e5ff';
      ctx.lineWidth = 1;

      ctx.fillRect(bar.x - bar.width / 2, bar.y - bar.height / 2, bar.width, bar.height);
      ctx.strokeRect(bar.x - bar.width / 2, bar.y - bar.height / 2, bar.width, bar.height);
      ctx.restore();
    }
  }

  private drawBullets(ctx: CanvasRenderingContext2D): void {
    for (const b of this.engine.bullets) {
      ctx.save();
      ctx.fillStyle = b.isEnemy ? '#ff3333' : '#00ffff';
      ctx.shadowColor = ctx.fillStyle;
      ctx.shadowBlur = 6;

      ctx.beginPath();
      ctx.arc(b.x, b.y, b.radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  private drawPowerups(ctx: CanvasRenderingContext2D): void {
    for (const p of this.engine.powerups) {
      ctx.save();
      ctx.translate(p.x, p.y);

      ctx.strokeStyle = p.type === 'spread' ? '#ff00ff' : p.type === 'rapid' ? '#ffff00' : '#00ff88';
      ctx.lineWidth = 2;
      ctx.shadowColor = ctx.strokeStyle;
      ctx.shadowBlur = 8;

      ctx.beginPath();
      ctx.rect(-p.radius, -p.radius, p.radius * 2, p.radius * 2);
      ctx.stroke();

      ctx.restore();
    }
  }

  private drawParticles(ctx: CanvasRenderingContext2D): void {
    for (const pt of this.engine.particles) {
      ctx.save();
      ctx.fillStyle = pt.color;
      ctx.globalAlpha = Math.max(0, pt.life / pt.maxLife);
      ctx.fillRect(pt.x - pt.size / 2, pt.y - pt.size / 2, pt.size, pt.size);
      ctx.restore();
    }
  }

  resize(w: number, h: number): void {
    if (!this.canvas) return;
    this.canvas.width = Math.max(320, w);
    this.canvas.height = Math.max(240, h);
  }

  getHud(): { score: number; lives: number; wave: number; message?: string } {
    return {
      score: this.engine.score,
      lives: this.engine.player.lives,
      wave: this.engine.wave,
      message: this.engine.message,
    };
  }

  dispose(): void {
    this.isDisposed = true;
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    if (this.canvas?.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }
  }
}
